/**
 * Scenario View page component for viewing existing scenarios
 */
import { ContentCopy } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { scenarioEconomyApi } from '../api/scenarioEconomy';
import { scenarioLayersApi } from '../api/scenarioLayers';
import { scenariosApi } from '../api/scenarios';
import { scenarioWellsApi } from '../api/scenarioWells';

import { ScenarioLayer } from '../api/scenarioLayers';
import { ScenarioWell } from '../api/scenarioWells';
import { GeneralStepView } from '../components/Scenarios/GeneralStepView';
import { ScenarioModelStep } from '../components/Scenarios/ScenarioModelStep';
import { ScenarioEconomyView } from '../components/Scenarios/ScenarioEconomyView';
import { ScenarioLayersView } from '../components/Scenarios/ScenarioLayersView';
import { ScenarioEconomicResultsStep } from '../components/Scenarios/ScenarioEconomicResultsStep';
import { ScenarioCalculationsStep } from '../components/Scenarios/ScenarioCalculationsStep';
import {
    getScenarioStepLabel,
    ScenarioStep,
    ScenarioStepsSidebar,
    STEPS_SIDEBAR_COLLAPSED_WIDTH,
    STEPS_SIDEBAR_WIDTH,
} from '../components/Scenarios/ScenarioStepsSidebar';
import { useScenarioWorkflowMobile } from '@/contexts/ScenarioWorkflowMobileContext';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { ScenarioAnaloguesStep } from '../components/Scenarios/ScenarioAnaloguesStep';
import { ScenarioWellsView } from '../components/Scenarios/ScenarioWellsView';
import { ScenarioResponse } from '../types/scenario';
import { ScenarioEconomy } from '../types/scenarioEconomy';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';
import { parseScenarioStep, scenarioPathWithStep } from '@/utils/scenarioStep';

interface ScenarioViewPageProps {
  mainSidebarCollapsed?: boolean;
}

// Visibility status UUIDs
const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

const getVisibilityLabel = (scenario: ScenarioResponse): string => {
  const visibilityStatusId = scenario.visibility_status_id;
  if (visibilityStatusId === PUBLIC_STATUS_UUID) {
    return 'Public';
  } else if (visibilityStatusId === COMPANY_PUBLIC_UUID) {
    return 'Company Public';
  } else {
    return 'Private';
  }
};

export const ScenarioViewPage: React.FC<ScenarioViewPageProps> = ({
  mainSidebarCollapsed = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<ScenarioStep>(() =>
    parseScenarioStep(searchParams.get('step'), 'layers'),
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const { register: registerScenarioMobileNav } = useScenarioWorkflowMobile();
  const openStepsDrawer = useCallback(() => setStepsDrawerOpen(true), []);

  // Scenario data state
  const [scenario, setScenario] = useState<ScenarioResponse | null>(null);
  const [layers, setLayers] = useState<ScenarioLayer[]>([]);
  const [wells, setWells] = useState<ScenarioWell[]>([]);
  const [economy, setEconomy] = useState<ScenarioEconomy | null>(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const scenarioMode: 'ml' | 'manual' = scenario?.mode === 'manual' ? 'manual' : 'ml';
  useRegisterPageHelp('Scenario', getScenarioStepLabel(currentStep, true, scenarioMode));

  const canEdit = useMemo(() => {
    if (!scenario || !user) return false;
    const isFromUserOrg = scenario.organization_id === user.organization_id;
    const isPublic = scenario.visibility_status_id === PUBLIC_STATUS_UUID;
    return isFromUserOrg || (isPublic && isSuperAdmin);
  }, [scenario, user, isSuperAdmin]);

  // Load scenario data
  useEffect(() => {
    const loadScenarioData = async () => {
      if (!scenarioId) {
        setError('Scenario ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load scenario basic info
        const scenarioData = await scenariosApi.getScenario(scenarioId);
        setScenario(scenarioData);

        // Load scenario layers
        try {
          const layersData = await scenarioLayersApi.getScenarioLayers(scenarioId);
          setLayers(layersData);
        } catch (err) {
          console.warn('Failed to load scenario layers:', err);
          setLayers([]);
        }

        // Load scenario wells
        try {
          const wellsData = await scenarioWellsApi.getScenarioWells(scenarioId);
          setWells(wellsData);
        } catch (err) {
          console.warn('Failed to load scenario wells:', err);
          setWells([]);
        }

        // Load scenario economy
        try {
          const economyData = await scenarioEconomyApi.getScenarioEconomy(scenarioId);
          setEconomy(economyData);
        } catch (err) {
          console.warn('Failed to load scenario economy:', err);
          setEconomy(null);
        }

      } catch (err: any) {
        console.error('Failed to load scenario data:', err);
        setError(err.response?.data?.detail || 'Failed to load scenario data');
      } finally {
        setLoading(false);
      }
    };

    loadScenarioData();
  }, [scenarioId]);

  const handleStepChange = useCallback((step: ScenarioStep) => {
    setCurrentStep(step);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('step', step);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const fromUrl = parseScenarioStep(searchParams.get('step'), currentStep);
    if (fromUrl !== currentStep) {
      setCurrentStep(fromUrl);
    }
    // Sync when query string changes (browser back/forward or mode switch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSwitchToEdit = useCallback(() => {
    if (!scenarioId || !canEdit) return;
    navigate(scenarioPathWithStep(`/app/scenarios/${scenarioId}/edit`, currentStep));
  }, [scenarioId, canEdit, currentStep, navigate]);

  const handleBack = useCallback(() => {
    navigate('/app/scenarios');
  }, [navigate]);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed]);

  const handleCopyScenario = useCallback(async () => {
    if (!scenarioId) {
      return;
    }
    try {
      setCopyBusy(true);
      setCopyError(null);
      const created = await scenariosApi.copyScenario(scenarioId, {});
      navigate(scenarioPathWithStep(`/app/scenarios/${created.id}/edit`, currentStep));
    } catch (err: unknown) {
      console.error('Failed to copy scenario:', err);
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setCopyError(
        typeof detail === 'string' ? detail : 'Failed to copy scenario. Please try again.'
      );
    } finally {
      setCopyBusy(false);
    }
  }, [scenarioId, navigate, currentStep]);

  useEffect(() => {
    if (!isMobile || !scenario) {
      registerScenarioMobileNav(null);
      return;
    }
    registerScenarioMobileNav({
      onOpen: openStepsDrawer,
      title: getScenarioStepLabel(currentStep, true, scenarioMode),
      readOnly: true,
    });
    return () => registerScenarioMobileNav(null);
  }, [isMobile, currentStep, scenario, scenarioMode, registerScenarioMobileNav, openStepsDrawer]);

  const getStepContent = useCallback(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography>Loading scenario data...</Typography>
        </Box>
      );
    }

    if (error || !scenario) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="error" sx={{ maxWidth: '600px' }}>
            {error || 'Scenario not found'}
          </Alert>
        </Box>
      );
    }

    switch (currentStep) {
      case 'general':
        return (
          <GeneralStepView
            scenario={scenario}
            onBack={handleBack}
          />
        );
      case 'layers':
        return (
          <ScenarioLayersView
            scenarioId={scenarioId!}
            layers={layers}
            scenario={scenario}
            onBack={handleBack}
          />
        );
      case 'model':
        return (
          <ScenarioModelStep
            scenarioId={scenarioId!}
            readOnly
            coordinate={scenario?.coordinate ?? null}
          />
        );
      case 'analogs':
        return (
          <ScenarioAnaloguesStep
            scenarioId={scenarioId!}
            readOnly
          />
        );
      case 'wells':
        return (
          <ScenarioWellsView
            scenarioId={scenarioId!}
            wells={wells}
            layers={layers}
            scenario={scenario}
            onBack={handleBack}
          />
        );
      case 'economy':
        return (
          <ScenarioEconomyView
            scenarioId={scenarioId!}
            economy={economy}
            scenario={scenario}
            onBack={handleBack}
          />
        );
      case 'calculations':
        return (
          <ScenarioCalculationsStep
            scenarioId={scenarioId!}
            readOnly={true}
          />
        );
      case 'results':
        return (
          <ScenarioEconomicResultsStep scenarioId={scenarioId!} readOnly />
        );
      default:
        return 'Select a step to continue';
    }
  }, [currentStep, loading, error, scenario, layers, wells, economy, scenarioId, handleBack]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading scenario...</Typography>
      </Box>
    );
  }

  if (error || !scenario) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: '600px' }}>
          {error || 'Scenario not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          flexDirection: 'row',
          width: '100%',
        }}
      >
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              maxWidth: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.3s ease',
            }}
          >
            <ScenarioStepsSidebar
              currentStep={currentStep}
              onStepChange={handleStepChange}
              collapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
              mainSidebarCollapsed={mainSidebarCollapsed}
              readOnly={true}
              scenarioMode={scenarioMode}
              canSwitchMode={canEdit}
              onSwitchMode={handleSwitchToEdit}
            />
          </Box>
        )}

        {isMobile && (
          <ScenarioStepsSidebar
            variant="temporary"
            open={stepsDrawerOpen}
            onClose={() => setStepsDrawerOpen(false)}
            currentStep={currentStep}
            onStepChange={handleStepChange}
            collapsed={false}
            readOnly={true}
            scenarioMode={scenarioMode}
            canSwitchMode={canEdit}
            onSwitchMode={handleSwitchToEdit}
          />
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
        >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" gutterBottom>
              {scenario.name}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {scenario.description || 'No description provided'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Created: {new Date(scenario.created_at).toLocaleDateString()} |
              Status: {scenario.is_draft ? 'Draft' : 'Published'} |
              Visibility: {getVisibilityLabel(scenario)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyScenario}
            disabled={copyBusy}
            sx={{ flexShrink: 0 }}
          >
            Copy
          </Button>
        </Box>

        {/* Step Content */}
        <Box sx={{ minHeight: 'calc(100vh - 200px)' }}>
          {getStepContent()}
        </Box>
        </Box>
      </Box>

      {/* Error Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!copyError}
        autoHideDuration={6000}
        onClose={() => setCopyError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopyError(null)} severity="error" sx={{ width: '100%' }}>
          {copyError}
        </Alert>
      </Snackbar>
    </Box>
  );
};
